// App.tsx - 인증 게이트 + 단일 페이지 탭 라우팅(react-router 없음 - AGENTS.md).
//
// 어느 화면인가는 **주소가 소유한다** (`lib/route.ts`). 화면 하나 = 주소 하나라
// 링크를 보내면 같은 화면이 열리고, 새로고침·뒤로가기가 화면 단위로 동작한다.
//   - `/<탭>`              어느 탭인가 (`Shell.tsx` 의 `NAV` 가 단일 소스)
//   - `/items/<id>`        선택된 개발품. 있으면 개발품 탭이 상세화면으로 바뀐다
//                          (결정 S-2: 개발품을 특정하지 않고 상세를 열 수 없다)
//   - `/erp/<모듈>/<메뉴>` 열린 ERP 메뉴
// 여기가 직접 소유하는 상태는 `toast`(5초 되돌리기를 포함한 일시 알림) 정도다.
//
// 마스터(담당자·고객사·공정)는 셸 수명 동안 거의 바뀌지 않고 여러 화면이
// 함께 쓰므로 여기서 한 번 읽어 내려준다. 화면마다 다시 읽으면 같은 목록을
// 네 번 가져온다.
import { DevItemForm } from "@/components/DevItemForm";
import {
  AppSidebar,
  AppTopBar,
  HIDDEN_SCREENS,
  type ScreenId,
  type ShellUser,
} from "@/components/Shell";
import { Button, Card, EmptyState, Icon, Spinner, Toast } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { type Me, getImpersonation, getMe, setImpersonation } from "@/lib/api";
import { useCustomers, useProcesses, useStaff } from "@/lib/hooks";
import { type AppRoute, navigateTo, parseRoute, pathFor } from "@/lib/route";
import { useIsMobile } from "@/lib/useViewport";
import { AdminScreen } from "@/screens/AdminScreen";
import { CalendarScreen } from "@/screens/CalendarScreen";
import { DashboardScreen } from "@/screens/DashboardScreen";
import { DevItemDetailScreen } from "@/screens/DevItemDetailScreen";
import { DevItemsScreen } from "@/screens/DevItemsScreen";
import { ErpScreen } from "@/screens/ErpScreen";
import { IssuesScreen } from "@/screens/IssuesScreen";
import { LoginScreen } from "@/screens/LoginScreen";
import { MasterScreen } from "@/screens/MasterScreen";
import { MyTasksScreen } from "@/screens/MyTasksScreen";
import type { ScreenNav } from "@/screens/nav";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * 화면 제목은 계획서 §3 의 원문 라벨을 그대로 쓴다. 사이드바는 264px 폭에
 * 맞춰 축약하므로, 원문 충실성은 여기가 담보한다 (REQ-NAV-001).
 */
const TITLES: Record<ScreenId, { title: string; crumbs?: string }> = {
  overview: { title: "개발품 종합현황" },
  items: { title: "개발품 상세관리" },
  schedule: { title: "일정관리" },
  issues: { title: "지연 및 이슈관리" },
  erp: { title: "ERP 업무", crumbs: "기존 ERP 이관" },
  mytasks: { title: "내 업무" },
  master: { title: "마스터 / 설정" },
  feedback: { title: "개선 · 이슈" },
  superadmin: { title: "슈퍼어드민" },
};

const FullPage = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      minHeight: "100dvh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 14,
      background: "var(--surface-sunken)",
      color: "var(--text-primary)",
      fontFamily: "var(--font-ui)",
    }}
  >
    {children}
  </div>
);

/** 되돌리기 토스트의 표시 시간 (결정 S-10). */
const UNDO_MS = 5000;
const TOAST_MS = 2600;

function PmsApp({ route }: { route: AppRoute }) {
  const { user, signOut } = useAuth();
  // 숨긴 화면(`HIDDEN_SCREENS`)의 주소는 종합현황으로 연다.
  const screen: ScreenId = HIDDEN_SCREENS.has(route.screen) ? "overview" : route.screen;
  const itemId = screen === "items" ? (route.itemId ?? null) : null;
  const erpView = screen === "erp" ? route.erpView : undefined;
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState<{ message: string; tone: "success" | "error" } | null>(null);
  const [undo, setUndo] = useState<(() => void | Promise<void>) | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const impersonation = getImpersonation();

  const customers = useCustomers();
  const staff = useStaff();
  const processes = useProcesses();

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // 타이머는 언마운트에서 반드시 정리한다.
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  const showToast = useCallback(
    (
      message: string,
      tone: "success" | "error" = "success",
      action?: () => void | Promise<void>
    ) => {
      setToast({ message, tone });
      setUndo(action ? () => action : null);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(
        () => {
          setToast(null);
          setUndo(null);
        },
        action ? UNDO_MS : TOAST_MS
      );
    },
    []
  );

  // 주소창을 정규 주소로 바로잡는다 (`/Items/`, `/overview`, 모르는 경로 → `/` …).
  // 기록을 쌓지 않아야 뒤로가기가 잘못된 주소로 되돌아가지 않는다.
  const canonical = pathFor({ screen, itemId: itemId ?? undefined, erpView });
  useEffect(() => {
    navigateTo(canonical, { replace: true });
  }, [canonical]);

  const go = useCallback((r: AppRoute) => {
    setDrawerOpen(false);
    navigateTo(pathFor(r));
  }, []);

  // 슈퍼어드민도 `pathFor` 가 `/admin` 으로 보낸다 - 탭이 아니라 별도 라우트다.
  const navigate = useCallback((id: ScreenId) => go({ screen: id }), [go]);

  const openErpView = useCallback(
    (key: string, replace = false) =>
      navigateTo(pathFor({ screen: "erp", erpView: key }), { replace }),
    []
  );

  const nav: ScreenNav = useMemo(
    () => ({
      goto: navigate,
      openItem: (id) => go({ screen: "items", itemId: id }),
      openCreate: () => {
        go({ screen: "items" });
        setCreateOpen(true);
      },
      toast: (message, tone = "success") => showToast(message, tone),
      toastUndo: (message, action) => showToast(message, "success", action),
    }),
    [go, navigate, showToast]
  );

  const exitImpersonation = () => {
    setImpersonation(null);
    window.location.reload();
  };

  const userName =
    me?.name || user?.displayName || user?.email?.split("@")[0] || impersonation?.name || "사용자";
  const shellUser: ShellUser = {
    name: userName,
    role: me?.position || "담당",
    dept: me?.dept || "개발관리팀",
    initial: userName.slice(0, 1).toUpperCase(),
  };
  const head = TITLES[screen];

  // 회사가 배정되지 않은 계정은 어떤 데이터도 볼 수 없다 (§04 §1).
  const noCompany = me !== null && !me.companyId;

  const body = noCompany ? (
    <Card>
      <EmptyState
        icon="alert"
        title="계정에 회사가 배정되지 않았습니다"
        description="관리자에게 문의해 주세요. 회사가 배정되면 개발품 현황을 볼 수 있어요."
        action={
          <Button variant="secondary" size="md" onClick={() => void signOut()}>
            로그아웃
          </Button>
        }
      />
    </Card>
  ) : screen === "overview" ? (
    <DashboardScreen nav={nav} customers={customers.data} staff={staff.data} />
  ) : screen === "items" ? (
    itemId ? (
      <DevItemDetailScreen
        itemId={itemId}
        nav={nav}
        onBack={() => go({ screen: "items" })}
        customers={customers.data}
        staff={staff.data}
        processes={processes.data}
        drawingsEnabled={me?.imagesEnabled ?? false}
      />
    ) : (
      <DevItemsScreen nav={nav} customers={customers.data} staff={staff.data} />
    )
  ) : screen === "schedule" ? (
    <CalendarScreen nav={nav} customers={customers.data} staff={staff.data} />
  ) : screen === "issues" ? (
    <IssuesScreen nav={nav} customers={customers.data} staff={staff.data} />
  ) : screen === "erp" ? (
    <ErpScreen nav={nav} me={me} viewKey={erpView} onOpenView={openErpView} />
  ) : screen === "mytasks" ? (
    <MyTasksScreen nav={nav} me={me} staff={staff.data} />
  ) : screen === "master" ? (
    <MasterScreen
      nav={nav}
      me={me}
      staffRes={staff}
      customersRes={customers}
      processesRes={processes}
    />
  ) : (
    <DashboardScreen nav={nav} customers={customers.data} staff={staff.data} />
  );

  return (
    <div style={{ display: "flex", minHeight: "100dvh", background: "var(--surface-sunken)" }}>
      {!isMobile && (
        <AppSidebar
          active={screen}
          onNavigate={navigate}
          user={shellUser}
          onProfileClick={() => navigate("master")}
          onCreateItem={nav.openCreate}
          showSuperadmin={me?.isSuperadmin ?? false}
        />
      )}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {impersonation && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 18px",
              background: "var(--state-warn)",
              color: "var(--text-on-accent)",
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon name="users" size={14} />
              {impersonation.name} ({impersonation.email}) 계정으로 로그인됨 · 임퍼소네이션
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={exitImpersonation}
              style={{
                border: "1px solid var(--border-strong)",
                background: "transparent",
                color: "inherit",
                borderRadius: "var(--radius-control)",
                padding: "4px 12px",
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              원래 계정으로 복귀
            </button>
          </div>
        )}
        <AppTopBar
          title={head.title}
          crumbs={head.crumbs}
          query={query}
          setQuery={setQuery}
          onLogout={() => void signOut()}
          onMenu={isMobile ? () => setDrawerOpen(true) : undefined}
          onFeedback={HIDDEN_SCREENS.has("feedback") ? undefined : () => navigate("feedback")}
          feedbackActive={screen === "feedback"}
        />
        <main style={{ flex: 1, minWidth: 0 }}>
          <div className="mx-auto w-full max-w-[1600px] px-s4 py-s5 sm:px-s5">{body}</div>
        </main>
      </div>

      {/* 등록 폼은 어느 화면에서 열어도 같은 것이어야 하므로 셸이 소유한다. */}
      <DevItemForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        customers={customers.data}
        staff={staff.data}
        processes={processes.data}
        onSaved={(created, message) => {
          showToast(message);
          go({ screen: "items", itemId: created.id });
        }}
        onError={(m) => showToast(m, "error")}
      />

      {/* 모바일 드로어. 애니메이션은 .jw-scrim / .jw-drawer 클래스가 갖는다 -
          인라인 keyframes 는 prefers-reduced-motion 이 손댈 수 없다.
          z 는 z-sheet(50). 60 을 쓰면 토스트 위로 올라간다. */}
      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-sheet">
          <button
            type="button"
            aria-label="메뉴 닫기"
            onClick={() => setDrawerOpen(false)}
            className="jw-scrim absolute inset-0 cursor-pointer border-0 p-0"
            style={{ background: "var(--scrim)" }}
          />
          <div
            className="jw-drawer absolute bottom-0 left-0 top-0"
            style={{ boxShadow: "var(--shadow-overlay)" }}
          >
            <AppSidebar
              active={screen}
              onNavigate={navigate}
              user={shellUser}
              onProfileClick={() => setDrawerOpen(false)}
              onCreateItem={nav.openCreate}
              showSuperadmin={me?.isSuperadmin ?? false}
              alwaysOpen
              style={{ height: "100dvh", overflowY: "auto" }}
            />
          </div>
        </div>
      )}

      <Toast
        message={toast?.message ?? null}
        tone={toast?.tone}
        action={
          undo ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                const run = undo;
                setToast(null);
                setUndo(null);
                void run();
              }}
            >
              되돌리기
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * `/admin` 게이트. 슈퍼어드민 여부는 **서버가 정한다**(`/api/me` 의
 * `is_superadmin` ← `SUPERADMIN_EMAILS`). 여기서 막는 것은 UI 편의일 뿐이고,
 * 진짜 방어는 API 쪽 `require_superadmin` 이다 - 이 분기를 우회해도 데이터는
 * 한 건도 나오지 않는다.
 *
 * 미인가에게는 403 이 아니라 앱 본체를 준다. 서버가 `/api/superadmin/*` 에
 * 404 로 존재를 숨기는 것과 같은 태도다 - "권한이 없다"는 응답 자체가
 * 콘솔의 존재를 알려준다.
 */
function AdminRoute() {
  const [me, setMe] = useState<Me | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((m) => !cancelled && setMe(m))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // 콘솔을 볼 수 없으면 앱 본체를 준다 - 주소도 `/` 로 바로잡혀 콘솔의 존재가 드러나지 않는다.
  if (failed) return <PmsApp route={{ screen: "overview" }} />;
  if (me === null)
    return (
      <FullPage>
        <span style={{ color: "var(--accent)" }}>
          <Spinner size={36} />
        </span>
      </FullPage>
    );
  if (!me.isSuperadmin) return <PmsApp route={{ screen: "overview" }} />;
  return <AdminScreen onExit={() => navigateTo("/")} />;
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));

  // 뒤로가기와 앱 내 이동이 같은 구독자를 지나도록 `popstate` 하나만 듣는다
  // (`navigateTo` 가 pushState 후 직접 쏜다).
  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (isLoading)
    return (
      <FullPage>
        <span style={{ color: "var(--accent)" }}>
          <Spinner size={36} />
        </span>
      </FullPage>
    );
  // 임퍼소네이션 토큰이 있으면 Firebase 로그인 없이도 진입 - 서버가 매 요청 토큰을
  // 검증하므로 안전하며, LLM+Playwright 가 키로 발급한 토큰만으로 앱을 구동할 수 있다.
  if (!isAuthenticated && !getImpersonation()) return <LoginScreen />;
  if (route.kind === "admin") return <AdminRoute />;
  return <PmsApp route={route} />;
}
